import { useSetStoreProcessTaskCompletion } from '@gozoki/api'
import type { StorePlanningShift, StorePlanningTask, StoreProcessTaskType } from '@gozoki/api-types'
import { cn } from '@gozoki/ui/lib/utils'
import { useEffect, useState } from 'react'
import closeIcon from '~/assets/dashboard/close.svg'
import {
  CleanIcon,
  MessageMultipleIcon,
  PackageDeliveredIcon,
  ShelvingUnitIcon,
  Truck24Icon,
} from './icons/dashboard-icons'
import { minutesOf } from './planning/planning-constants'

const TASK_ICON: Record<StoreProcessTaskType, typeof CleanIcon> = {
  CLEANING: CleanIcon,
  RECEPTION: PackageDeliveredIcon,
  LIVRAISON: Truck24Icon,
  INVENTORY: ShelvingUnitIcon,
  CLIENT_RELATION: MessageMultipleIcon,
}

const initialsOf = (name: string): string =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)

// « Prénom » + « NOM » séparés pour l'affichage en carte (Figma : prénom en gros, nom en petit).
const splitName = (name: string): { first: string; last: string } => {
  const parts = name.trim().split(/\s+/)
  return { first: parts[0] ?? name, last: parts.slice(1).join(' ') }
}

const formatDate = (isoDate: string): string => {
  const date = new Date(`${isoDate}T00:00:00`)
  if (Number.isNaN(date.getTime())) return isoDate
  const label = date.toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: '2-digit',
    month: 'long',
  })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

// Créneaux dont l'horaire chevauche la plage de la tâche (même jour) — collaborateurs proposés
// comme auteurs de la validation.
const shiftsOnTask = (
  task: StorePlanningTask,
  shifts: StorePlanningShift[],
): StorePlanningShift[] => {
  const taskFrom = minutesOf(task.startTime)
  const taskTo = minutesOf(task.endTime)
  return shifts.filter((shift) => {
    const from = minutesOf(shift.startTime)
    const to = minutesOf(shift.endTime) <= from ? 1440 : minutesOf(shift.endTime)
    return !(to <= taskFrom || from >= taskTo)
  })
}

type Outcome = 'ok' | 'issue'

// Réponse « bien réalisée ? » déduite du résultat déjà enregistré (récap d'une tâche revalidée).
const outcomeFromStatus = (status: StorePlanningTask['completionStatus']): Outcome | null => {
  if (status === 'WITHOUT_ISSUES') return 'ok'
  if (status === 'WITH_ISSUES') return 'issue'
  return null
}

/**
 * Modale de validation d'une tâche « process » (Figma 11450:54540), ouverte au clic sur une tâche
 * des widgets Process / Planning ou sur le CTA doré (prochaine tâche).
 *
 * On désigne le collaborateur qui a réalisé la tâche, on indique si l'action s'est bien passée ou
 * non (résultat tracé : `completionStatus`), avec un commentaire libre optionnel. « Enregistrer »
 * marque la tâche faite (PATCH guard `store`).
 */
export function TaskCompletionModal({
  task,
  shifts,
  onClose,
}: {
  task: StorePlanningTask
  shifts: StorePlanningShift[]
  onClose: () => void
}) {
  const onShift = shiftsOnTask(task, shifts)
  const Icon = TASK_ICON[task.type]
  const { mutate, isPending } = useSetStoreProcessTaskCompletion()

  // Récap : une tâche déjà validée rouvre avec l'auteur, le résultat et le commentaire enregistrés.
  const [selectedCollabId, setSelectedCollabId] = useState<number | null>(task.doneByCollabId)
  const [outcome, setOutcome] = useState<Outcome | null>(outcomeFromStatus(task.completionStatus))
  const [comment, setComment] = useState(task.issueComment ?? '')

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  // On exige un auteur (dès qu'un collaborateur est en poste) ET une réponse « bien réalisée ? ».
  const canSave = (onShift.length === 0 || selectedCollabId !== null) && outcome !== null

  const save = () => {
    if (!outcome) return
    mutate(
      {
        id: task.id,
        completed: true,
        doneByCollabId: selectedCollabId ?? undefined,
        completionStatus: outcome === 'ok' ? 'WITHOUT_ISSUES' : 'WITH_ISSUES',
        issueComment: comment.trim() || undefined,
      },
      { onSuccess: onClose },
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(7,44,26,0.25)] p-[32px]"
      role="dialog"
      aria-modal="true"
      aria-label={`Validation ${task.label}`}
      onClick={onClose}
    >
      <div
        className="flex max-h-full w-[908px] max-w-full flex-col gap-[24px] overflow-auto kiosk-scroll-y rounded-[24px] bg-[#F0EDE2] p-[32px] drop-shadow-[0px_8px_20px_rgba(0,0,0,0.15)]"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-[12px]">
          <span className="flex shrink-0 items-center justify-center rounded-[12px] bg-[#F9F8F1] p-[8px] text-[#4F6A47]">
            <Icon className="size-[24px]" />
          </span>
          <h2 className="min-w-0 flex-1 truncate font-brand text-[22px] leading-[26px] font-semibold text-[#4F6A47]">
            {task.label}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="shrink-0 cursor-pointer p-[2px] -m-[2px]"
          >
            <span className="flex size-[40px] items-center justify-center rounded-[8px] bg-[#F9F8F1]">
              <img src={closeIcon} alt="" aria-hidden className="size-[16px]" />
            </span>
          </button>
        </div>

        <span className="h-px w-full bg-[#0000000F]" />

        {/* Récap tâche */}
        <div className="flex items-center gap-[16px] rounded-[12px] bg-[#F9F8F1] p-[24px]">
          <span className="flex size-[48px] shrink-0 items-center justify-center rounded-[8px] bg-[#F0EDE2] text-[#4F6A47]">
            <Icon className="size-[24px]" />
          </span>
          <span className="min-w-0 flex-1 truncate font-brand text-[22px] leading-[26px] font-extrabold text-[#152013]">
            {task.label}
          </span>
          <span className="h-[40px] w-px shrink-0 bg-[#0000000F]" />
          <div className="flex shrink-0 flex-col items-end gap-[6px]">
            <span className="font-sans text-[13px] font-semibold text-[#7C867D]">Date</span>
            <span className="font-brand text-[18px] leading-[22px] font-extrabold text-[#6A9560]">
              {formatDate(task.date)}
            </span>
          </div>
          <span className="h-[40px] w-px shrink-0 bg-[#0000000F]" />
          <div className="flex shrink-0 flex-col items-end gap-[6px]">
            <span className="font-sans text-[13px] font-semibold text-[#7C867D]">Heure</span>
            <span className="font-brand text-[18px] leading-[22px] font-extrabold text-[#4F6A47]">
              {task.startTime}
            </span>
          </div>
        </div>

        {/* Description (consignes saisies au back-office) — lecture seule, masquée si absente */}
        {task.description ? (
          <div className="flex flex-col gap-[10px]">
            <span className="font-sans text-[14px] font-semibold text-[#58655B]">Description</span>
            <p className="rounded-[12px] bg-[#F9F8F1] p-[24px] font-sans text-[14px] font-medium whitespace-pre-line text-[#152013]">
              {task.description}
            </p>
          </div>
        ) : null}

        {/* Sélection du collaborateur */}
        <div className="flex flex-col gap-[10px]">
          <span className="font-sans text-[14px] font-semibold text-[#58655B]">
            Sélectionner le personnel qui s'est occupé de la tâche
          </span>
          {onShift.length === 0 ? (
            <span className="font-sans text-[14px] text-[#8A887F]">
              Personne en shift sur cette plage.
            </span>
          ) : (
            <div className="flex flex-wrap gap-[8px]">
              {onShift.map((shift) => {
                const active = selectedCollabId === shift.collaboratorId
                const { first, last } = splitName(shift.collaboratorName)
                return (
                  <button
                    key={shift.id}
                    type="button"
                    onClick={() => setSelectedCollabId(active ? null : shift.collaboratorId)}
                    aria-pressed={active}
                    className={cn(
                      'flex min-w-[140px] flex-1 cursor-pointer flex-col items-center gap-[10px] rounded-[12px] border p-[16px]',
                      active ? 'border-[#6A9560] bg-[#E4EDDB]' : 'border-[#0000000F] bg-[#F9F8F1]',
                    )}
                  >
                    {shift.profilePictureUrl ? (
                      <img
                        src={shift.profilePictureUrl}
                        alt=""
                        className="size-[74px] rounded-full object-cover"
                      />
                    ) : (
                      <span className="flex size-[74px] items-center justify-center rounded-full bg-[#CEE3C4] text-[20px] font-semibold text-[#354A31]">
                        {initialsOf(shift.collaboratorName)}
                      </span>
                    )}
                    <span className="flex w-full flex-col items-center gap-[2px]">
                      <span className="truncate font-brand text-[20px] leading-[24px] font-semibold text-[#4F6A47]">
                        {first}
                      </span>
                      {last ? (
                        <span className="truncate font-sans text-[13px] font-semibold text-[#58655B] uppercase">
                          {last}
                        </span>
                      ) : null}
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Résultat : bien réalisée ? */}
        <div className="flex flex-col gap-[10px]">
          <span className="font-sans text-[14px] font-semibold text-[#58655B]">
            L'action a-t-elle été bien réalisée ?
          </span>
          <div className="flex gap-[8px]">
            <OutcomeCard
              active={outcome === 'ok'}
              onClick={() => setOutcome('ok')}
              glyph="✓"
              title="Oui"
              subtitle="Tout s'est bien passé"
              tone="ok"
            />
            <OutcomeCard
              active={outcome === 'issue'}
              onClick={() => setOutcome('issue')}
              glyph="✕"
              title="Non"
              subtitle="J'ai rencontré un souci"
              tone="issue"
            />
          </div>
        </div>

        {/* Commentaire optionnel */}
        <div className="flex flex-col gap-[10px]">
          <span className="font-sans text-[14px] font-semibold text-[#58655B]">
            Commentaire supplémentaire (Optionnel)
          </span>
          <textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Ex : Il manquait le produit de nettoyage,…"
            className="h-[104px] w-full resize-none rounded-[10px] bg-[#F9F8F1] p-[24px] font-sans text-[14px] font-medium text-[#152013] outline-none placeholder:text-[#AAB4AC]"
          />
        </div>

        <span className="h-px w-full bg-[#0000000F]" />

        {/* Actions */}
        <div className="flex gap-[20px]">
          <button
            type="button"
            onClick={onClose}
            className="flex h-[52px] flex-1 cursor-pointer items-center justify-center rounded-[8px] bg-[#F9F8F1] font-sans text-[14px] font-semibold text-[#4F6A47]"
          >
            Fermer
          </button>
          <button
            type="button"
            disabled={!canSave || isPending}
            onClick={save}
            className={cn(
              'flex h-[52px] flex-1 items-center justify-center rounded-[8px] bg-[#6A9560] font-sans text-[14px] font-semibold text-[#F9F8F1]',
              canSave && !isPending ? 'cursor-pointer' : 'opacity-50',
            )}
          >
            Enregistrer l'action
          </button>
        </div>
      </div>
    </div>
  )
}

function OutcomeCard({
  active,
  onClick,
  glyph,
  title,
  subtitle,
  tone,
}: {
  active: boolean
  onClick: () => void
  glyph: string
  title: string
  subtitle: string
  tone: 'ok' | 'issue'
}) {
  // Carte + pastille : neutre tant que non sélectionnée, sinon teinte verte (ok) ou orange (souci).
  let cardTone = 'border-transparent bg-[#F9F8F1]'
  let glyphTone = 'bg-[#F0EDE2] text-[#8A887F]'
  if (active) {
    cardTone = tone === 'ok' ? 'border-[#B8D9A8] bg-[#E4EDDB]' : 'border-[#EF8031] bg-[#FBF1E7]'
    glyphTone = tone === 'ok' ? 'bg-[#CEE3C4] text-[#4F6A47]' : 'bg-[#F6D8BF] text-[#B85B1C]'
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex flex-1 cursor-pointer items-center gap-[24px] rounded-[12px] border p-[24px] text-left',
        cardTone,
      )}
    >
      <span
        className={cn(
          'flex size-[48px] shrink-0 items-center justify-center rounded-[8px] text-[22px] font-bold',
          glyphTone,
        )}
        aria-hidden
      >
        {glyph}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-[4px]">
        <span className="font-brand text-[22px] leading-[26px] font-semibold text-[#4F6A47]">
          {title}
        </span>
        <span className="font-sans text-[14px] font-semibold text-[#58655B]">→ {subtitle}</span>
      </span>
    </button>
  )
}
